import { useMsal } from "@azure/msal-react";
import {
    ChoiceGroup, DefaultButton,
    IPanelProps,
    IRenderFunction,
    MessageBar,
    MessageBarType,
    Panel,
    PanelType,
    PrimaryButton,
    Separator
} from "@fluentui/react";
import { RootState } from "app/store";
import { EViewType, createGroupNumber, emitCustomEvent, fadeIn, panelButtonsStyles } from "global/Application";
import { useApplication } from "global/Application/Context";
import { MsalTokenSource } from "global/DataSources/MsalTokenSource";
import { updateCESNumber } from "global/Reducer/dataSlice";
import * as IXrmOptions from "model/AttributeOptionSets.type";
import { IPanelEvent, IValueChoiceGroupOption } from "model/Extension";
import * as FAM from "model/FlexibleAttributeModel";
import { useEffect, useState } from "react";
import { ConnectedProps, connect, useDispatch, useSelector } from "react-redux";
import { AppDataSource } from "../../global/DataSources/AppDataSource";
import { EView } from "./GroupStructure/EView.type";
import GroupStructureCESEditor from "./GroupStructure/GroupStructureCESEditor";
import { IGroupStructureCESWizardItem } from "./GroupStructure/GroupStructureCESWizard/IGroupStructureCESWizardItem.type";
import GroupStructureDepartmentWizard from "./GroupStructure/GroupStructureDepartmentWizard";
import GroupStructureImportWizard from "./GroupStructure/GroupStructureImportWizard";

interface IConfirm {
    tierLevels: FAM.ITierLevel[] | null;
    benefit: FAM.IBenefit | null;
    benefitName?: string;
}

export enum EGroupStructureWizardVariant {
    wizard = 1,
    export = 2,
}
interface IProps extends TComponentConnectedProps {
    //TODO: unmount component with delay for animation
    isOpen: boolean;

    // cancellation of transaction/partial actions
    onCancel?: () => void;

    // dialog closure request from parent component
    onDismiss: () => void

    // use this prior to completion of primary action for verification/or to perform action on parent
    onConfirm?: (e?: IPanelEvent<IConfirm>) => void;

    // start condition
    defaultPayload?: IConfirm;

    variant?: EGroupStructureWizardVariant | null;
}



export interface TLog {
    date: Date;
    type: 'warn' | 'error' | 'info' | 'ok';
    row: number | null;
    message: string;
}


const GroupStructureWizard = (props: IProps) => {

    const dispatch = useDispatch();
    const { instance } = useMsal();

    const application = useApplication();

    const state = useSelector((state: RootState) => state);

    const [view, setView] = useState<EView | null>(EView.entry);
    const [validationMessage, setValidationMessage] = useState<string | null>(null);

    const readonly = props.state.status.viewType === EViewType.ViewOnly;

    const isAdminChange = props?.state?.data?.json?.changeType?.value === IXrmOptions.bcbs_groupconfigurationsnapshot.bcbs_changetypecode['Administrative Change(808900003)'].value;

    //Entry
    const [entryOption, setEntryOption] = useState<IValueChoiceGroupOption<EView> | null>(null);
    const entryOptions: IValueChoiceGroupOption<EView>[] = [
        { key: 'addCES', text: 'Add CES Client Number', data: EView.ces, disabled: readonly || application.ChangeTypePermissions.canAddGroupStructure },
        { key: 'addGroupNumber', text: 'Add Group Number', data: EView.addGroupNumber, disabled: readonly || application.ChangeTypePermissions.canAddGroupStructure },
        { key: 'addDept', text: 'Add Department', data: EView.department, disabled: readonly || application.ChangeTypePermissions.canAddGroupStructure },
        { key: 'export', text: 'Export', data: EView.export, },
        { key: 'import', text: 'Import', data: EView.import, disabled: readonly || isAdminChange },
    ];

    const tokenSource = new MsalTokenSource(instance);
    const dataSource = new AppDataSource(tokenSource);

    const styleItem = { display: 'flex', alignItems: 'center' };

    // startup use effect
    useEffect(() => {
        // Status Initialization
        if (props.isOpen) {

            // only export mode
            if (props.variant === EGroupStructureWizardVariant.export) {
                setView(EView.export);
                setEntryOption(entryOptions.find(q => q.data === EView.export)!);
            }
            else {
                setView(EView.entry);
            }
        }

    }, [props.isOpen]);

    useEffect(() => {
        if (view === EView.import) {
            if (isAdminChange)
                setView(EView.export);
        }
    }, [view]);


    const onRenderEntry = () => {
        return <div>
            <ChoiceGroup
                options={entryOptions}
                label="Choose Source"
                required={true}
                selectedKey={entryOption?.key}
                onChange={(ev, option?: IValueChoiceGroupOption<EView>) => {
                    setEntryOption(option ?? null);
                }}
            />
        </div >;
    }

    const onRenderEntryFooter = () => {
        return <div className={fadeIn}>
            <PrimaryButton text='Continue'
                styles={panelButtonsStyles}
                onClick={() => {
                    if (entryOption?.data === EView.addGroupNumber) {

                        emitCustomEvent({
                            eventType: 'NewGroupConfiguration',
                            details: createGroupNumber(props.state.data)
                        })

                        props.onDismiss?.();
                    } else {
                        setView(entryOption?.data ?? null);
                    }
                }}
            />

            <DefaultButton
                text='Cancel'
                onClick={() => { props.onCancel?.(); }}
                styles={panelButtonsStyles}
            />
        </div>;
    }

    const onAddCESNumber = (change: FAM.ICESNumber) => {
        const list = state.data.json.groupStructure.cesNumbers.map(eachCesNumber => {
            const t: IGroupStructureCESWizardItem = {
                oldCES: { ...eachCesNumber },
                newCES: { ...eachCesNumber },
                status: null,
            }
            return t;
        });

        list.push({
            newCES: change,
            oldCES: null,
            status: 'untracked'
        });

        dispatch(updateCESNumber(list));
        props.onDismiss();
    }

    const onRenderCES = () => {
        return <GroupStructureCESEditor
            isOpen={props.isOpen}
            isAdd={true}
            onChange={(event, value) => onAddCESNumber(value)}
            onCancel={() => setView(EView.entry)}
            onDismiss={props.onDismiss}
        />
    }

    const onRenderDepartment = () => {
        return <GroupStructureDepartmentWizard
            isOpen={props.isOpen}
            onCancel={props.onCancel}
            onDismiss={props.onDismiss}
            setView={setView}
        />
    }

    const onRenderExport = () => {
        return <GroupStructureImportWizard
            key="export"
            variant="export"
            isOpen={props.isOpen}
            onCancel={props.onCancel}
            onDismiss={props.onDismiss}
            setView={setView}
        />
    }


    const onRenderImport = () => {
        return <GroupStructureImportWizard
            key="import"
            variant="import"
            isOpen={props.isOpen}
            onCancel={props.onCancel}
            onDismiss={props.onDismiss}
            setView={setView}
        />
    }


    const onRenderBody: IRenderFunction<IPanelProps> = (bodyProps, defaultRender) => {
        let onRender = onRenderEntry;


        // lets pick a right body for rending based on active view
        switch (view) {
            case EView.entry:
                onRender = onRenderEntry;
                break;

            case EView.ces:
                onRender = onRenderCES;
                break;

            case EView.department:
                onRender = onRenderDepartment;
                break;

            case EView.export:
                onRender = onRenderExport;
                break;

            case EView.import:
                onRender = onRenderImport;
                break;

            default:
                break;
        }

        return (
            <div>
                <Separator />
                {validationMessage &&
                    <>
                        <MessageBar className={fadeIn} messageBarType={MessageBarType.error} onDismiss={() => setValidationMessage("")}                >
                            {validationMessage}
                        </MessageBar>
                    </>
                }
                {onRender()}
            </div>
        );
    }

    const onRenderFooter: IRenderFunction<IPanelProps> = (footerProps, defaultRender) => {
        let onRender = onRenderEntryFooter;

        // lets pick a right body for rending based on active view
        switch (view) {
            case EView.entry:
                onRender = onRenderEntryFooter;
                break;

            default:
                break;
        }

        return onRender();
    }

    return <Panel
        isOpen={props.isOpen}
        onDismiss={props.onCancel}
        type={PanelType.medium}
        layerProps={{ eventBubblingEnabled: true }}
        isLightDismiss
        headerText="Manage | Group Structure"
        onRenderFooterContent={onRenderFooter}
        isFooterAtBottom={true}
    >
        {onRenderBody()}
    </Panel>
}

function mapStateToProps(state: RootState) { return { state } }
const connector = connect(mapStateToProps);
type TComponentConnectedProps = ConnectedProps<typeof connector>;
export default connector(GroupStructureWizard);